/**
 * A dose that was not given, and why (KIN-1841/KIN-1849).
 *
 * The backend splits these two ways because HL7 table 0322 does, and because the HSE
 * lists them separately in what a practice must notify: `RE` is the patient or parent
 * refusing, `NA` is a dose that simply was not administered. "Declined" is the wrong word
 * for a contraindication — nobody declined it, the clinician withheld it — so the two are
 * labelled and grouped apart rather than lumped together as refusals.
 */
export const REFUSAL_OUTCOME = {
  refused: "REFUSED",
  notAdministered: "NOT_ADMINISTERED",
};

const OUTCOME_LABELS = {
  [REFUSAL_OUTCOME.refused]: "Declined",
  [REFUSAL_OUTCOME.notAdministered]: "Not administered",
};

// The server derives `outcome` from the stored reason code, so it is null only for a code
// the list no longer knows — nothing writes one today, but such a row is still shown
// rather than dropped, and this is what it reads as.
const UNKNOWN_OUTCOME_LABEL = "Not given";

export const refusalOutcomeLabel = (outcome) =>
  OUTCOME_LABELS[outcome] || UNKNOWN_OUTCOME_LABEL;

export const isClinicalNonAdministration = (refusal) =>
  refusal?.outcome === REFUSAL_OUTCOME.notAdministered;

/**
 * Whether a record still has an effect on what is outstanding. A withdrawn refusal stays
 * in the record — the vaccine having once been declined is itself clinically meaningful —
 * but stops counting against the schedule.
 */
export const isStandingRefusal = (refusal) => !refusal?.withdrawn;

/**
 * Split a patient's records into the ones that still apply and the ones that have been
 * withdrawn, newest refusal first within each.
 *
 * Sorted here rather than relied upon from the server: the list is read as a history, and
 * a clinician scanning it wants the most recent decision first.
 */
export const groupRefusals = (refusals) => {
  const byNewestFirst = [...(refusals || [])].sort((left, right) =>
    String(right.refusedOn || "").localeCompare(String(left.refusedOn || ""))
  );

  return {
    standing: byNewestFirst.filter(isStandingRefusal),
    withdrawn: byNewestFirst.filter((refusal) => !isStandingRefusal(refusal)),
  };
};
